import { useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';

import { useCloudStatus } from '../../hooks/use-cloud-status';
import {
	getProductQueryParamTeamType,
	USER_SEGMENTATION_TEAM_TYPES,
	UserSegmentationTeamTypes,
} from '../../models/user-segmentation';
import { getProjectKeyFromTeamType } from '../setup/jira/utils';

import { jsmUseCaseProvisioningMessages } from './jsm-use-case-provisioning-messages';

const JSM_USE_CASE_PROJECT_CREATE_TIMEOUT_MS = 60_000;

export type JsmUseCaseProjectStatus = 'idle' | 'creating' | 'complete' | 'error' | 'timeout';

export type UseJsmUseCaseProjectCreateReturn = {
	isSimplifiedProject: boolean | undefined;
	projectKey: string | undefined;
	projectStatus: JsmUseCaseProjectStatus;
};

type UseJsmUseCaseProjectCreateParams = {
	templateId: string | undefined;
	teamType: string | undefined;
};

export const useJsmUseCaseProjectCreate = ({
	templateId,
	teamType,
}: UseJsmUseCaseProjectCreateParams): UseJsmUseCaseProjectCreateReturn => {
	const intl = useIntl();
	const { cloudId, createCloudStatus } = useCloudStatus();
	const stargateClient = useBxpWebStargateClient();

	const hasCreatedRef = useRef(false);

	const [projectStatus, setProjectStatus] = useState<JsmUseCaseProjectStatus>('idle');
	const [projectKey, setProjectKey] = useState<string | undefined>();
	const [isSimplifiedProject, setIsSimplifiedProject] = useState<boolean | undefined>();

	useEffect(() => {
		const hasCloudCreationFailed =
			createCloudStatus === 'error' || createCloudStatus === 'error-unrecoverable';
		const hasCloudCreationTimedOut =
			createCloudStatus === 'timeout' || createCloudStatus === 'timeout-recoverable';

		if (hasCloudCreationFailed || hasCloudCreationTimedOut) {
			setProjectStatus(hasCloudCreationTimedOut ? 'timeout' : 'error');
			return;
		}

		if (
			!cloudId ||
			!templateId ||
			!teamType ||
			createCloudStatus !== 'success' ||
			hasCreatedRef.current
		) {
			return;
		}

		hasCreatedRef.current = true;

		const createProject = async () => {
			setProjectStatus('creating');

			const resolvedProjectKey = getProjectKeyFromTeamType(teamType);
			const resolvedTeamType = getProductQueryParamTeamType(teamType);
			const resolvedProjectName = intl.formatMessage(
				USER_SEGMENTATION_TEAM_TYPES.includes(teamType as UserSegmentationTeamTypes)
					? jsmUseCaseProvisioningMessages.defaultProjectName[teamType as UserSegmentationTeamTypes]
					: jsmUseCaseProvisioningMessages.defaultProjectNameFallback,
			);

			try {
				const { taskId } = await stargateClient.postProjectCreate({
					cloudId,
					customDetail: {
						enableExtraFields: true,
					},
					issueTypes: [],
					projectKey: resolvedProjectKey,
					projectName: resolvedProjectName,
					statusNames: [],
					teamType: resolvedTeamType,
					template: templateId,
				});

				const result = await stargateClient.pollTaskUntilComplete(
					taskId,
					cloudId,
					JSM_USE_CASE_PROJECT_CREATE_TIMEOUT_MS,
				);

				if (result.status !== 'COMPLETE') {
					setProjectStatus('error');
					return;
				}

				if (!result.result?.projectId) {
					setProjectStatus('error');
					return;
				}

				let createdProjectKey = resolvedProjectKey;
				let createdProjectIsSimplified: boolean | undefined;

				try {
					const projectDetailsResponse = await stargateClient.fetch(
						`/ex/jira/${cloudId}/rest/api/3/project/${result.result.projectId}`,
						{
							credentials: 'include',
							method: 'GET',
						},
					);

					if (!projectDetailsResponse.ok) {
						throw new Error('Project details lookup failed');
					}

					const projectDetails =
						(await projectDetailsResponse.json()) as JiraProjectDetailsResponse;

					createdProjectKey = projectDetails.key || resolvedProjectKey;
					createdProjectIsSimplified = projectDetails.simplified;
				} catch {
					// Project creation succeeded, but the landing-shape lookup failed.
					// /browse/{projectKey} works for simplified projects and is the safer fallback.
					createdProjectIsSimplified = true;
				}

				setProjectKey(createdProjectKey);
				setIsSimplifiedProject(createdProjectIsSimplified);
				setProjectStatus('complete');
			} catch (error) {
				const isTimeout = error instanceof Error && error.message.includes('timed out');
				setProjectStatus(isTimeout ? 'timeout' : 'error');
			}
		};

		void createProject();
	}, [cloudId, createCloudStatus, intl, stargateClient, teamType, templateId]);

	return { isSimplifiedProject, projectKey, projectStatus };
};

type JiraProjectDetailsResponse = {
	readonly key?: string;
	readonly simplified?: boolean;
};
